import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'astroturf/react';
import { FeedContext } from 'js/social/contexts/feed';
import { SuggestionsContext } from 'js/social/contexts/suggestions';
import { SuggestionCard } from 'js/social/suggestions/SuggestionCard';
import { Suggestion } from 'js/social/shared/types';
import { Body } from 'js/design_system/Body';
import { ViewAllSuggestions } from 'js/social/suggestions/ViewAllSuggestions';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { getSocialImpressionSource } from '../ImpressionHelper';
import { Carousel } from 'js/social/shared/Carousel';
import { serializeUserSuggestionScoringMetadata } from 'js/social/ImpressionHelper';

const Container = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  padding: 8px 0;
  background: $ds-white;

  @include medium-up {
    border: 1px solid #ebebee;
    border-radius: 6px;
  }
`;

const Header = styled(Body)`
  padding: 8px 12px 0 12px;
`;

const CarouselContainer = styled.div`
  padding: 8px 0;
`;

// A group must contain at least this many suggestions to be show in the carousel
const MIN_GROUP_SIZE = 4;

export const SuggestionsCarousel: React.FC<{ index: number }> = ({ index }) => {
  const {
    isLoading,
    getGroup,
    getRandomGroupId,
    getFirstPriorityGroupId,
    getSuggestions,
  } = useContext(SuggestionsContext);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const {
    feedConfig: { page },
  } = useContext(FeedContext);

  const group = useMemo(() => {
    return getGroup(groupId);
  }, [groupId, getGroup]);

  useEffect(() => {
    if (!isLoading && !groupId) {
      const newGroupId = getFirstPriorityGroupId(MIN_GROUP_SIZE);

      if (newGroupId) {
        setGroupId(newGroupId);
        setSuggestions(getSuggestions(newGroupId, 10));
      }
    }
  }, [
    isLoading,
    groupId,
    getRandomGroupId,
    getFirstPriorityGroupId,
    getSuggestions,
  ]);

  const cards: (Suggestion | undefined)[] = useMemo(() => {
    return [...suggestions, undefined];
  }, [suggestions]);

  if (isLoading || !group || !groupId) {
    return null;
  }

  return (
    <Container>
      <Header>{group.name}</Header>

      <CarouselContainer>
        <Carousel
          items={cards}
          fixedItemWidth={220}
          gap={16}
          keyExtractor={(item) => (item ? item.user.id : 'view-all')}
          renderItem={(item, itemIndex) =>
            item ? (
              <ImpressionWatcher
                key={item.user.id}
                source={getSocialImpressionSource(
                  page,
                  `feed-${index}`,
                  'sugg-f',
                )}
                entity_type={ImpressionEntity.User}
                entity_id={item.user.id}
                extra={{
                  category: groupId,
                  card_index: itemIndex,
                  num_cards: suggestions.length,
                  scoring: serializeUserSuggestionScoringMetadata(
                    item.scoring_metadata,
                  ),
                }}
              >
                <SuggestionCard
                  suggestion={item}
                  suggestionSource={groupId}
                  followSource="social_news_feed_carousel"
                  onIgnore={() =>
                    setSuggestions(
                      suggestions.filter((other) => item !== other),
                    )
                  }
                />
              </ImpressionWatcher>
            ) : (
              <ImpressionWatcher
                source={getSocialImpressionSource(
                  page,
                  `feed-${index}`,
                  'sugg-a',
                )}
                extra={{
                  category: groupId,
                  card_index: suggestions.length,
                }}
              >
                <ViewAllSuggestions />
              </ImpressionWatcher>
            )
          }
        />
      </CarouselContainer>
    </Container>
  );
};
